'use client'

import { useState, useEffect } from 'react'
import { ChapterWithHsCodesDto } from '@/modules/hs/types/hs-response'
import SidebarV2 from './SidebarV2'
import ChapterGroup from './ChapterGroup'
import type { Chapter } from './TOC'
import { Star, Eye, HelpCircle, BookOpen } from 'lucide-react'

interface SearchResultsLayoutProps {
  tree: Array<{
    id: number
    chapters: ChapterWithHsCodesDto[]
  }>
  tocChapters: Chapter[]
  highlightTerm?: string
  isDefaultView?: boolean
}

export default function SearchResultsLayout({ tree, tocChapters, highlightTerm, isDefaultView = false }: SearchResultsLayoutProps) {
  // All chapters are visible by default
  const [visibleChapterIds, setVisibleChapterIds] = useState<Set<string>>(
    new Set(tocChapters.map((c) => c.id))
  )
  // All visible chapters are expanded by default, unless isDefaultView is true
  const [expandedChapterIds, setExpandedChapterIds] = useState<Set<string>>(
    isDefaultView ? new Set() : new Set(tocChapters.map((c) => c.id))
  )
  const [activeChapter, setActiveChapter] = useState(tocChapters[0]?.id || '')
  const [showScrollTop, setShowScrollTop] = useState(false)

  // Update visible and expanded chapters when tocChapters change
  useEffect(() => {
    setVisibleChapterIds(new Set(tocChapters.map((c) => c.id)))
    setExpandedChapterIds(isDefaultView ? new Set() : new Set(tocChapters.map((c) => c.id)))
  }, [tocChapters, isDefaultView])

  const handleToggleSelectAll = () => {
    if (visibleChapterIds.size === tocChapters.length) {
      setVisibleChapterIds(new Set())
    } else {
      setVisibleChapterIds(new Set(tocChapters.map((c) => c.id)))
    }
  }

  const handleToggleChapterVisibility = (id: string) => {
    const next = new Set(visibleChapterIds)
    if (next.has(id)) {
      next.delete(id)
    } else {
      next.add(id)
    }
    setVisibleChapterIds(next)
  }

  const handleToggleChapterExpansion = (id: string) => {
    const next = new Set(expandedChapterIds)
    if (next.has(id)) {
      next.delete(id)
    } else {
      next.add(id)
    }
    setExpandedChapterIds(next)
  }

  const handleExpandAll = () => {
    setExpandedChapterIds(new Set(visibleChapterIds))
  }

  const handleCollapseAll = () => {
    setExpandedChapterIds(new Set())
  }

  const handleScrollToChapter = (id: string) => {
    // Only scroll if chapter is visible
    if (!visibleChapterIds.has(id)) return

    setActiveChapter(id)

    const element = document.getElementById(id)
    if (element) {
      const headerOffset = 90
      const elementPosition = element.getBoundingClientRect().top
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset
      window.scrollTo({ top: offsetPosition, behavior: 'smooth' })
    }
  }

  // Intersection Observer for active chapter tracking
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.intersectionRatio >= 0.1) {
            setActiveChapter(entry.target.id)
          }
        })
      },
      { rootMargin: '-10% 0px -70% 0px', threshold: [0.1] }
    )

    const chapterElements = document.querySelectorAll('[data-chapter-section]')
    chapterElements.forEach((el) => observer.observe(el))

    return () => observer.disconnect()
  }, [visibleChapterIds])

  // Scroll detection for "Scroll to Top" button
  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 300)
    }

    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  const scrollToTop = () => {
    document.getElementById('search-input')?.scrollIntoView({ behavior: 'smooth' })
  }

  return (
    <>
      <div className="content-layout">
        {/* Sidebar */}
        {<SidebarV2
          chapters={tocChapters}
          expandedChapterIds={visibleChapterIds}
          activeChapter={activeChapter}
          onToggleSelectAll={handleToggleSelectAll}
          onToggleChapterExpansion={handleToggleChapterVisibility}
          onScrollToChapter={handleScrollToChapter}
        />}

        {/* Results Container */}
        <div className="results-container">
          {visibleChapterIds.size === 0 ? (
            <div className="chapter-group">
              <div className="p-8 text-center bg-slate-50 rounded-xl border-2 border-dashed border-slate-200">
                <p className="text-slate-600 font-semibold mb-2">Không có chương nào được chọn</p>
                <p className="text-slate-500 text-sm mb-4">
                  Vui lòng chọn các chương ở cột bên trái để hiển thị kết quả.
                </p>
                <button
                  onClick={handleToggleSelectAll}
                  className="px-6 py-2.5 bg-primary text-white rounded-xl font-bold text-sm shadow-lg hover:bg-primary-dark transition-all mb-6"
                >
                  Chọn tất cả các chương
                </button>
              </div>
            </div>
          ) : (
            <>
              {/* Toolbar */}
              <div className=" bg-slate-50 backdrop-blur-md p-4 rounded-xl border border-slate-50 shadow-sm mb-6">
                <div className="flex items-center justify-between gap-4">
                  <div className="flex items-center gap-2">
                    <button
                      onClick={handleCollapseAll}
                      className="flex items-center gap-2 px-4 py-2 text-sm font-bold text-slate-700 bg-white rounded-xl transition-all shadow-sm hover:bg-slate-50 hover:shadow-md cursor-pointer"
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.5"
                      >
                        <polyline points="4 14 10 14 10 20"></polyline>
                        <polyline points="20 10 14 10 14 4"></polyline>
                        <line x1="14" y1="10" x2="21" y2="3"></line>
                        <line x1="3" y1="21" x2="10" y2="14"></line>
                      </svg>
                      Thu gọn
                    </button>
                    <button
                      onClick={handleExpandAll}
                      className="flex items-center gap-2 px-4 py-2 text-sm font-bold text-slate-700 bg-white rounded-xl transition-all shadow-sm hover:bg-slate-50 hover:shadow-md cursor-pointer"
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.5"
                      >
                        <polyline points="15 3 21 3 21 9"></polyline>
                        <polyline points="9 21 3 21 3 15"></polyline>
                        <line x1="21" y1="3" x2="14" y2="10"></line>
                        <line x1="3" y1="21" x2="10" y2="14"></line>
                      </svg>
                      Mở rộng hết
                    </button>
                  </div>
                  <div className="flex items-center gap-4 px-4 py-1.5 bg-white rounded-xl border border-slate-100 shadow-inner">
                    <div className="flex items-center gap-2 text-sm font-bold text-slate-500">
                      <Star size={14} fill="currentColor" className="text-amber-500" />
                      Yêu thích
                    </div>
                    <div className="w-px h-3 bg-slate-200"></div>
                    <div className="flex items-center gap-2 text-sm font-bold text-slate-500">
                      <Eye size={14} className="text-primary" />
                      Xem gần đây
                    </div>
                  </div>
                </div>
              </div>

              {/* Chapters */}
              {tree.map((part) =>
                part.chapters
                  .filter((chapter) => visibleChapterIds.has(`ch-${chapter.id}`))
                  .map((chapter) => {
                    const chapterId = `ch-${chapter.id}`
                    return (
                      <div key={chapter.id} id={chapterId} data-chapter-section>
                        <ChapterGroup
                          chapter={chapter}
                          isExpanded={expandedChapterIds.has(chapterId)}
                          onToggleExpand={() => handleToggleChapterExpansion(chapterId)}
                          highlightTerm={highlightTerm}
                        />
                      </div>
                    )
                  })
              )}
            </>
          )}
        </div>
      </div>

      {/* Scroll to Top Button */}
      <button
        onClick={scrollToTop}
        className={`cursor-pointer fixed bottom-8 right-8 z-50 p-3 bg-white text-primary rounded-full shadow-lg hover:shadow-xl transition-all border border-slate-200 hover:bg-slate-50 ${showScrollTop
          ? 'opacity-100 translate-y-0'
          : 'opacity-0 translate-y-4 pointer-events-none'
          }`}
        aria-label="Scroll to top"
      >
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <polyline points="18 15 12 9 6 15"></polyline>
        </svg>
      </button>
    </>
  )
}
